/*
 *   File : swp-table-config.js
 *   Author : https://evoqins.com
 *   Description : SWP table column configuration
 *   Integrations : NA
 *   Version : 1.0.0
*/

import React from 'react';
import { Chip } from '../../Components/Others';
import { TextButton } from '../../Components/Buttons';

const COLUMN_WIDTHS = {
    registeredDate: "120px",
    customerId: "100px",
    customerName: "200px",
    scheme: "260px",
    executedSwps: "90px",
    swpAmount: "120px",
    rayiStatus: "110px",
    fpStatus: "110px",
    action: "110px",
};

const th = (label) => <span className="e-table-th-wrap">{label}</span>;
const thLines = (...lines) => (
    <span className="e-table-th-wrap">
        {lines.map((line, i) => (
            <React.Fragment key={i}>
                {i > 0 ? <br /> : null}
                {line}
            </React.Fragment>
        ))}
    </span>
);

const displayValue = (value) => {
    if (value === null || value === undefined || value === "" || value === "-") {
        return "-";
    }
    return value;
};

const getStatusChipClass = (status) => {
    if (!status) return "e-bg-bleu-de-france";
    const normalized = String(status).toLowerCase();
    if (normalized === "active" || normalized === "success" || normalized === "successful" || normalized === "confirmed") {
        return "e-bg-green-crayola";
    }
    if (normalized === "failed" || normalized === "cancelled" || normalized === "canceled") {
        return "e-bg-maximum-red";
    }
    if (normalized === "inactive") {
        return "e-bg-taupe-gray";
    }
    if (normalized === "pending" || normalized === "under_review") {
        return "e-bg-marigold";
    }
    return "e-bg-bleu-de-france";
};

export const getSwpColumns = ({ onViewDetails, includeCustomerColumns = true } = {}) => {
    const columns = [
        {
            name: th('Registered Date'),
            selector: row => row.setup_date || row.registered_date || "-",
            cell: row =>
                <div className='line-height-20px'>
                    <p className='mb-0 e-font-weight-500'>
                        {row.registered_date || "-"}
                    </p>
                    <p className='mb-0 '>
                        {row.registered_time || ""}
                    </p>
                </div>,
            width: COLUMN_WIDTHS.registeredDate,
            sortable: true,
            id: "registered_date",
        },
    ];

    if (includeCustomerColumns) {
        columns.push(
            {
                name: th('Customer ID'),
                selector: row => displayValue(row.rayi_customer_id),
                width: COLUMN_WIDTHS.customerId,
                sortable: true,
                id: "customer_id",
            },
            {
                name: th('Customer Name'),
                selector: row => row.customer_name,
                cell: row => <div className='line-height-20px text-truncate' title={displayValue(row.customer_name)}>{displayValue(row.customer_name)}</div>,
                width: COLUMN_WIDTHS.customerName,
                sortable: true,
                id: "customer_name",
            },
        );
    }

    columns.push(
        {
            name: th('Scheme'),
            cell: row => (
                <p className='mb-0 truncate-third-line line-height-20px text-truncate' title={displayValue(row.scheme)}>
                    {displayValue(row.scheme)}
                </p>
            ),
            width: COLUMN_WIDTHS.scheme,
            id: "scheme",
        },
        {
            name: thLines('Executed / Total', 'SWPs'),
            selector: row => displayValue(row.installment_text),
            width: COLUMN_WIDTHS.executedSwps,
            id: "executed_swps",
        },
        {
            name: th('SWP amount'),
            selector: row => row.swp_amount,
            cell: row => <>{displayValue(row.swp_amount_text)}</>,
            width: COLUMN_WIDTHS.swpAmount,
            sortable: true,
            id: "swp_amount",
        },
        {
            name: thLines('Rayi SWP', 'Status'),
            cell: row => row.rayi_swp_status ?
                <Chip label={row.rayi_swp_status}
                    className={getStatusChipClass(row.rayi_swp_status)} />
                : "-",
            width: COLUMN_WIDTHS.rayiStatus,
            id: "rayi_swp_status",
        },
        {
            name: thLines('FP SWP', 'status'),
            cell: row => row.fp_swp_status ?
                <Chip label={row.fp_swp_status}
                    className={getStatusChipClass(row.fp_swp_status)} />
                : "-",
            width: COLUMN_WIDTHS.fpStatus,
            id: "fp_swp_status",
        },
    );

    if (onViewDetails) {
        columns.push({
            name: '',
            cell: row => (
                <TextButton name="View details"
                    fontSize="e-font-14"
                    fontWeight="e-font-weight-600"
                    onClick={() => onViewDetails(row)} />
            ),
            width: COLUMN_WIDTHS.action,
            ignoreRowClick: true,
            button: true,
            id: "action",
        });
    }

    return columns;
};
